/**
 * WIZARD_RightToLeft_v01 (React + TypeScript + React-Bootstrap)
 * Version: v01c-typehelp-submit-ux
 * Date: 2025-09-05 (America/Denver)
 *
 * Adds (v01c):
 * - Help text animation triggers on each slide change (none | typewriter | fade)
 * - Finish button shows a tip if form not ready (missing requireds)
 * - Finish is disabled immediately on click; whole form greys out during submission
 * - Successful submit -> Summary view with user data
 */

import React, {
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
  useEffect,
  useRef,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Carousel,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

/* ============================================================
   Declarations (tunable variables & types)
   ============================================================ */
const VERSION = "v01c-typehelp-submit-ux";
const SHOW_VERSION = true; // toggle version badge on/off
const TEST_MODE = true; // true = console.log payload; false = POST to API
const API_GATEWAY_URL =
  "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/submit"; // <-- replace with your URL

const SLIDE_ANIMATION_MS = 450; // carousel slide duration
const SHOW_PROGRESS_BAR = true;

// --- Help text effect controls ---
type HelpEffect = "none" | "typewriter" | "fade";

/** Choose the global help text effect here */
const HELP_EFFECT: HelpEffect = "typewriter"; // "none" | "typewriter" | "fade"

/** Typewriter tunables */
const TYPEWRITER_SPEED_MS = 20; // smaller = faster
const TYPEWRITER_CURSOR = true; // show blinking cursor while typing

/** Fade tunables */
const FADE_DURATION_MS = 400; // CSS transition duration for fade

const LABELS = {
  title: "New Policy Wizard",
  subtitle: "Tell us about you and your business",
  stepTitles: ["Contact", "Business", "Operations", "Coverage", "Summary"],
  buttons: {
    back: "Back",
    next: "Next",
    finish: "Finish",
    edit: "Edit",
    submit: "Submit",
  },
  notices: {
    required: "Required fields are marked with *",
  },
} as const;

const STEP_HELP = [
  {
    title: "Why your contact details matter",
    text:
      "Accurate contact info lets us reach you with quotes, clarifications, and bindable offers without delay.",
  },
  {
    title: "Why we need business details",
    text:
      "Underwriters price risk based on your business profile (years active, structure, employees). Clear data yields accurate pricing.",
  },
  {
    title: "Operational risk questions",
    text:
      "Insurers consider where and how you work (residential vs. commercial vs. industrial) to tailor coverage and premium.",
  },
  {
    title: "Coverage preferences",
    text:
      "Your limits, deductibles, and additional coverages determine pricing and ensure your policy fits real exposures.",
  },
] as const;

/* ============================================================
   Types
   ============================================================ */
type WizardData = {
  // Step 1: Contact
  fullName: string;
  email: string;
  phone: string;

  // Step 2: Business
  businessName: string;
  yearsInBusiness: string;
  employees: string;

  // Step 3: Operations
  pctResidential: string;
  pctCommercial: string;
  pctIndustrial: string;

  // Step 4: Coverage
  generalLiabilityLimit: string;
  deductible: string;
  toolsAndEquipmentCoverage: string;
};

const defaultData: WizardData = {
  fullName: "",
  email: "",
  phone: "",

  businessName: "",
  yearsInBusiness: "",
  employees: "",

  pctResidential: "",
  pctCommercial: "",
  pctIndustrial: "",

  generalLiabilityLimit: "",
  deductible: "",
  toolsAndEquipmentCoverage: "",
};

/* ============================================================
   Helpers: Effects
   ============================================================ */

/** TypewriterText: progressively reveals `text` when `triggerKey` changes. */
const TypewriterText: React.FC<{
  text: string;
  speedMs?: number;
  showCursor?: boolean;
  triggerKey?: string | number; // force restart when this value changes
  className?: string;
}> = ({
  text,
  speedMs = TYPEWRITER_SPEED_MS,
  showCursor = TYPEWRITER_CURSOR,
  triggerKey,
  className,
}) => {
  const [idx, setIdx] = useState<number>(0);
  const timerRef = useRef<number | null>(null);

  // Restart typing when triggerKey or text changes
  useEffect(() => {
    setIdx(0);
  }, [triggerKey, text]);

  useEffect(() => {
    if (idx >= text.length) return;
    timerRef.current = window.setTimeout(() => setIdx((i) => i + 1), speedMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [idx, text.length, speedMs]);

  const isDone = idx >= text.length;
  return (
    <span className={className}>
      {text.slice(0, idx)}
      {showCursor && !isDone ? <span className="tw-cursor">|</span> : null}
    </span>
  );
};

/** FadeText: applies a simple mount fade-in. */
const FadeText: React.FC<{
  text: string;
  durationMs?: number;
  triggerKey?: string | number;
  className?: string;
}> = ({ text, durationMs = FADE_DURATION_MS, triggerKey, className }) => {
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    setVisible(false);
    const t = window.setTimeout(() => setVisible(true), 10); // next tick
    return () => window.clearTimeout(t);
  }, [triggerKey, text]);

  return (
    <span
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${durationMs}ms ease`,
        display: "inline-block",
      }}
    >
      {text}
    </span>
  );
};

/* ============================================================
   Small, focused UI helpers
   ============================================================ */
type HelpPanelProps = {
  title: string;
  text: string;
  className?: string;
  /** which effect to use for the body text (defaults to global HELP_EFFECT) */
  effect?: HelpEffect;
  /** a key that changes when the step changes to retrigger animations */
  triggerKey?: string | number;
};

const HelpPanel: React.FC<HelpPanelProps> = ({
  title,
  text,
  className,
  effect = HELP_EFFECT,
  triggerKey,
}) => {
  return (
    <Alert variant="info" className={`mb-3 ${className ?? ""}`.trim()}>
      <div className="fw-semibold mb-1">{title}</div>
      <div className="small text-muted">
        {effect === "typewriter" ? (
          <TypewriterText text={text} triggerKey={triggerKey} />
        ) : effect === "fade" ? (
          <FadeText text={text} triggerKey={triggerKey} />
        ) : (
          text
        )}
      </div>
    </Alert>
  );
};

type FieldNoteProps = PropsWithChildren<{ className?: string }>;
const FieldNote: React.FC<FieldNoteProps> = ({ children, className }) => (
  <div className={`form-text ${className ?? ""}`.trim()}>{children}</div>
);

/* ============================================================
   Step subcomponents (modular and reusable)
   ============================================================ */
type StepProps = {
  data: WizardData;
  setData: (partial: Partial<WizardData>) => void;
  /** activeIndex is passed in to re-trigger help animations on slide change */
  activeIndex: number;
};

const Step1Contact: React.FC<StepProps> = ({ data, setData, activeIndex }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[0].title}
        text={STEP_HELP[0].text}
        triggerKey={`help-${activeIndex}`}
      />

      <Form.Group className="mb-3">
        <Form.Label>Full Name *</Form.Label>
        <Form.Control
          value={data.fullName}
          onChange={(e) => setData({ fullName: e.target.value })}
          placeholder="Jane Doe"
          required
        />
        <FieldNote>We’ll reference this on your quote and policy docs.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Email *</Form.Label>
            <Form.Control
              type="email"
              value={data.email}
              onChange={(e) => setData({ email: e.target.value })}
              placeholder="name@example.com"
              required
            />
            <FieldNote>Used for sending quotes and policy updates.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone *</Form.Label>
            <Form.Control
              value={data.phone}
              onChange={(e) => setData({ phone: e.target.value })}
              placeholder="(555) 555-5555"
              required
            />
            <FieldNote>We’ll call/text for time-sensitive items.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step2Business: React.FC<StepProps> = ({ data, setData, activeIndex }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[1].title}
        text={STEP_HELP[1].text}
        triggerKey={`help-${activeIndex}`}
      />

      <Form.Group className="mb-3">
        <Form.Label>Business Name *</Form.Label>
        <Form.Control
          value={data.businessName}
          onChange={(e) => setData({ businessName: e.target.value })}
          placeholder="Acme Services LLC"
          required
        />
        <FieldNote>Exactly as shown on your license/docs when possible.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Years in Business *</Form.Label>
            <Form.Control
              value={data.yearsInBusiness}
              onChange={(e) => setData({ yearsInBusiness: e.target.value })}
              placeholder="e.g., 5"
              required
            />
            <FieldNote>More tenure can improve pricing with some carriers.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Employees *</Form.Label>
            <Form.Control
              value={data.employees}
              onChange={(e) => setData({ employees: e.target.value })}
              placeholder="e.g., 8"
              required
            />
            <FieldNote>Include owners if they perform field work.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step3Operations: React.FC<StepProps> = ({ data, setData, activeIndex }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[2].title}
        text={STEP_HELP[2].text}
        triggerKey={`help-${activeIndex}`}
      />

      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Residential *</Form.Label>
            <InputGroup>
              <Form.Control
                value={data.pctResidential}
                onChange={(e) => setData({ pctResidential: e.target.value })}
                placeholder="e.g., 60"
                required
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FieldNote>Typical home projects, service calls, etc.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Commercial *</Form.Label>
            <InputGroup>
              <Form.Control
                value={data.pctCommercial}
                onChange={(e) => setData({ pctCommercial: e.target.value })}
                placeholder="e.g., 30"
                required
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FieldNote>Retail/office/restaurant sites.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Industrial *</Form.Label>
            <InputGroup>
              <Form.Control
                value={data.pctIndustrial}
                onChange={(e) => setData({ pctIndustrial: e.target.value })}
                placeholder="e.g., 10"
                required
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FieldNote>Factories, plants, heavy facilities.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <div className="small text-muted mt-2">
        Tip: Ensure the three percentages sum to ~100%. Underwriters expect this.
      </div>
    </Card.Body>
  </Card>
);

const Step4Coverage: React.FC<StepProps> = ({ data, setData, activeIndex }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[3].title}
        text={STEP_HELP[3].text}
        triggerKey={`help-${activeIndex}`}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>General Liability Limit *</Form.Label>
            <Form.Select
              value={data.generalLiabilityLimit}
              onChange={(e) => setData({ generalLiabilityLimit: e.target.value })}
              required
            >
              <option value="">Select a limit…</option>
              <option>$1M / $2M</option>
              <option>$2M / $2M</option>
              <option>$2M / $4M</option>
            </Form.Select>
            <FieldNote>Common small-business options shown here.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Deductible *</Form.Label>
            <Form.Select
              value={data.deductible}
              onChange={(e) => setData({ deductible: e.target.value })}
              required
            >
              <option value="">Select a deductible…</option>
              <option>$500</option>
              <option>$1,000</option>
              <option>$2,500</option>
              <option>$5,000</option>
            </Form.Select>
            <FieldNote>Higher deductibles often reduce premium.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mt-3">
        <Form.Label>Tools & Equipment Coverage (desired limit)</Form.Label>
        <Form.Control
          value={data.toolsAndEquipmentCoverage}
          onChange={(e) =>
            setData({ toolsAndEquipmentCoverage: e.target.value })
          }
          placeholder="e.g., $25,000"
        />
        <FieldNote>
          Optional—helps cover theft/damage to your gear on job sites.
        </FieldNote>
      </Form.Group>
    </Card.Body>
  </Card>
);

/* ============================================================
   Summary view (read-only confirmation)
   ============================================================ */
const SummaryView: React.FC<{ data: WizardData }> = ({ data }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <h5 className="mb-3">Summary</h5>
      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Contact</h6>
          <div><strong>Name:</strong> {data.fullName || "-"}</div>
          <div><strong>Email:</strong> {data.email || "-"}</div>
          <div><strong>Phone:</strong> {data.phone || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Business</h6>
          <div><strong>Business Name:</strong> {data.businessName || "-"}</div>
          <div><strong>Years in Business:</strong> {data.yearsInBusiness || "-"}</div>
          <div><strong>Employees:</strong> {data.employees || "-"}</div>
        </Col>
      </Row>

      <hr />

      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Operations</h6>
          <div><strong>% Residential:</strong> {data.pctResidential || "-"}</div>
          <div><strong>% Commercial:</strong> {data.pctCommercial || "-"}</div>
          <div><strong>% Industrial:</strong> {data.pctIndustrial || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Coverage</h6>
          <div><strong>GL Limit:</strong> {data.generalLiabilityLimit || "-"}</div>
          <div><strong>Deductible:</strong> {data.deductible || "-"}</div>
          <div><strong>Tools & Equipment:</strong> {data.toolsAndEquipmentCoverage || "-"}</div>
        </Col>
      </Row>

      <Alert variant="secondary" className="mt-3 mb-0">
        Review your entries. If something looks off, tap <strong>Edit</strong> to go back.
      </Alert>
    </Card.Body>
  </Card>
);

/* ============================================================
   Main Component
   ============================================================ */
export const WIZARD_RightToLeft_v03: React.FC = () => {
  // activeIndex: 0..3 are data-entry slides, 4 is Summary
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [data, setData] = useState<WizardData>(defaultData);
  const [showFinishTip, setShowFinishTip] = useState<boolean>(false);
  const finishBtnRef = useRef<HTMLButtonElement | null>(null);

  // Shallow merge setter for convenience
  const patch = useCallback(
    (partial: Partial<WizardData>) => setData((prev) => ({ ...prev, ...partial })),
    []
  );

  const atFirst = activeIndex === 0;
  const atLastDataStep = activeIndex === 3;
  const atSummary = activeIndex === 4;

  // Derived progress (5 total slides including summary)
  const progress = useMemo(
    () => Math.round(((activeIndex + 1) / 5) * 100),
    [activeIndex]
  );

  const handlePrev = () => {
    if (activeIndex > 0) setActiveIndex((i) => i - 1);
  };

  const handleNext = () => {
    if (activeIndex < 3) setActiveIndex((i) => i + 1);
  };

  // Minimal presence validation (extend as needed)
  const requiredOk =
    data.fullName &&
    data.email &&
    data.phone &&
    data.businessName &&
    data.yearsInBusiness &&
    data.employees &&
    data.pctResidential &&
    data.pctCommercial &&
    data.pctIndustrial &&
    data.generalLiabilityLimit &&
    data.deductible;

  // Finish: validate minimal fields, then submit, then advance to Summary
  const handleFinish = async () => {
    setSubmitError(null);

    if (!requiredOk) {
      // Show a tip for a moment to guide the user
      setShowFinishTip(true);
      setTimeout(() => setShowFinishTip(false), 2500);
      return;
    }

    setSubmitting(true); // disable buttons & grey out
    try {
      const wire = {
        ...data, // keep strings; normalize/trim if desired
        meta: {
          testMode: TEST_MODE,
          version: VERSION,
          submittedAtISO: new Date().toISOString(),
        },
      };

      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 600)); // simulate latency
        // eslint-disable-next-line no-console
        console.log("[TEST_MODE] Submitting payload:", wire);
      } else {
        const res = await fetch(API_GATEWAY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(wire),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(
            `API error ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`
          );
        }
      }

      setActiveIndex(4); // Summary screen on success
    } catch (err: any) {
      setSubmitError(err?.message ?? "Unknown error submitting form.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = () => {
    setActiveIndex(0);
  };

  // Render per-step content; pass activeIndex so help animation re-triggers every slide change.
  const stepCards = [
    <Step1Contact key="s1" data={data} setData={patch} activeIndex={activeIndex} />,
    <Step2Business key="s2" data={data} setData={patch} activeIndex={activeIndex} />,
    <Step3Operations key="s3" data={data} setData={patch} activeIndex={activeIndex} />,
    <Step4Coverage key="s4" data={data} setData={patch} activeIndex={activeIndex} />,
    <SummaryView key="s5" data={data} />,
  ];

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 className="mb-0">{LABELS.title}</h3>
          <div className="text-muted">{LABELS.subtitle}</div>
        </Col>
        <Col className="text-end">
          {SHOW_VERSION && (
            <Badge bg="secondary" pill>
              {VERSION}
            </Badge>
          )}
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <div className="small text-muted">{LABELS.notices.required}</div>
        </Col>
      </Row>

      {SHOW_PROGRESS_BAR && (
        <Row className="mb-3">
          <Col>
            <ProgressBar now={progress} label={`${progress}%`} />
          </Col>
        </Row>
      )}

      {/* Headline for current step */}
      <Row className="mb-2">
        <Col>
          <h5 className="mb-0">
            {LABELS.stepTitles[activeIndex] ?? "Step"}
          </h5>
        </Col>
      </Row>

      {/* Submission grey-out wrapper */}
      <div className={submitting ? "wizard-submitting" : ""} aria-busy={submitting}>
        {/* Carousel: programmatic index; no built-in controls/indicators; slides animate */}
        <Carousel
          activeIndex={activeIndex}
          onSelect={() => {}}
          interval={undefined}
          controls={false}
          indicators={false}
          slide
          fade={false}
          className="mb-3"
        >
          {stepCards.map((card, i) => (
            <Carousel.Item
              key={`slide-${i}`}
              style={{ transitionDuration: `${SLIDE_ANIMATION_MS}ms` }}
            >
              {card}
            </Carousel.Item>
          ))}
        </Carousel>

        {/* Errors */}
        {submitError && (
          <Alert variant="danger" className="mb-3">
            {submitError}
          </Alert>
        )}

        {/* Footer actions */}
        <Row className="g-2">
          <Col xs="auto">
            <Button
              variant="outline-secondary"
              onClick={handlePrev}
              disabled={atFirst || submitting}
            >
              {LABELS.buttons.back}
            </Button>
          </Col>

          {!atSummary && !atLastDataStep && (
            <Col xs="auto">
              <Button variant="primary" onClick={handleNext} disabled={submitting}>
                {LABELS.buttons.next}
              </Button>
            </Col>
          )}

          {!atSummary && atLastDataStep && (
            <Col xs="auto">
              <OverlayTrigger
                placement="top"
                show={showFinishTip}
                overlay={
                  <Tooltip id="finish-tip">
                    Please complete all required fields marked with *.
                  </Tooltip>
                }
                target={finishBtnRef.current as any}
              >
                <span>
                  <Button
                    ref={finishBtnRef}
                    variant="success"
                    onClick={handleFinish}
                    disabled={submitting} // immediate disable once submitting
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Finishing…
                      </>
                    ) : (
                      LABELS.buttons.finish
                    )}
                  </Button>
                </span>
              </OverlayTrigger>
            </Col>
          )}

          {atSummary && (
            <>
              <Col xs="auto">
                <Button variant="outline-primary" onClick={handleEdit} disabled={submitting}>
                  {LABELS.buttons.edit}
                </Button>
              </Col>
              <Col xs="auto">
                {/* Optional: final Submit after reviewing Summary (disabled; Finish already submitted). */}
                <Button variant="primary" disabled>
                  {LABELS.buttons.submit}
                </Button>
              </Col>
            </>
          )}
        </Row>
      </div>

      {/* Styles for help effects and submission grey-out */}
      <style>{`
        .carousel-item > .card { width: 100%; }

        /* Typewriter cursor */
        .tw-cursor {
          display: inline-block;
          margin-left: 1px;
          animation: tw-blink 1s step-end infinite;
        }
        @keyframes tw-blink {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }

        /* Submission overlay feel (light grey + disable interactions) */
        .wizard-submitting {
          position: relative;
          pointer-events: none; /* block clicks */
          opacity: 0.6;
          filter: grayscale(20%);
        }
      `}</style>
    </Container>
  );
};
